import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Modal as MuiModal,
  SxProps,
  Theme,
  Typography,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import "./CustomModal.css";
import CustomSectionTitle from "../SectionTitle/CustomSectionTitle";
import CustomButton from "../Buttons/Button/CustomButton";

export type CustomModalType =
  | "create"
  | "flexColModal"
  | "otherModal"
  | "mediumModal"
  | "wideModal"
  | "deleteModal"
  | "registerModal";

export interface CustomModalProps {
  openModal: boolean;
  handleCloseModal: () => void;
  title: string;
  children?: React.ReactNode;
  modalType?: CustomModalType;
  bodyText?: string;
  addTitleBorder?: boolean | "true" | "false";
  bodyTextAlign?: "left" | "center" | "right";
  hideCloseButton?: boolean;
  sx?: SxProps<Theme>;
  /**
   * registerModal の固定 90vh 高を解除してコンテンツ合わせにする。
   * アップロード画面など縦に短いステップで使う想定。
   */
  dynamicHeight?: boolean;
  /**
   * 2カラム split レイアウト用の左ペイン。指定時のみ children が右ペインに収まる。
   * 主に OCR 結果モーダル等で 左:プレビュー / 右:フォーム の構成を作る。
   */
  leftPane?: React.ReactNode;
  /**
   * registerModal の最大幅を 648px → 1280px に拡張する。
   * split レイアウト (leftPane) 利用時に併用する想定。
   */
  wide?: boolean;
  /**
   * 未保存ガード: true のとき、閉じ操作 (× / 背景クリック / Esc) で破棄確認を出し、
   * 「破棄して閉じる」を選んだ時のみ handleCloseModal を呼ぶ。
   * 通常はフォームの dirty 状態 (例: react-hook-form の formState.isDirty) を渡す。
   */
  confirmOnDirtyClose?: boolean;
  /** 破棄確認の本文 (省略時はデフォルト文言)。 */
  dirtyCloseMessage?: string;
}

const resolveAddBorder = (value: CustomModalProps["addTitleBorder"]) => {
  if (typeof value === "string") return value === "true";
  return Boolean(value);
};

const mapModalTypeToClass = (modalType: CustomModalType) => {
  switch (modalType) {
    case "create":
      return "createModal";
    case "flexColModal":
      return "flexColModal";
    case "mediumModal":
      return "mediumModal";
    case "wideModal":
      return "wideModal";
    case "deleteModal":
      return "deleteModal";
    case "registerModal":
      return "registerModal";
    case "otherModal":
    default:
      return "otherModal";
  }
};

const CustomModal: React.FC<CustomModalProps> = ({
  openModal,
  handleCloseModal,
  title,
  children,
  modalType = "otherModal",
  bodyText = "",
  addTitleBorder = false,
  bodyTextAlign = "center",
  hideCloseButton = false,
  sx,
  dynamicHeight = false,
  leftPane,
  wide = false,
  confirmOnDirtyClose = false,
  dirtyCloseMessage = "入力中の情報が失われますが、よろしいでしょうか？",
}) => {
  const isMobile = useMediaQuery("(max-width:600px)");

  const modalTypeClass = mapModalTypeToClass(modalType);
  const addBorder = resolveAddBorder(addTitleBorder);
  const isSplit = !!leftPane;

  // 未保存ガード: 閉じ操作を一旦受けて、dirty なら破棄確認を挟む。
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const requestClose = () => {
    if (confirmOnDirtyClose) setShowDiscardConfirm(true);
    else handleCloseModal();
  };
  const confirmDiscard = () => {
    setShowDiscardConfirm(false);
    handleCloseModal();
  };
  const cancelDiscard = () => setShowDiscardConfirm(false);

  // モーダル本文がスクロールしたとき、ヘッダー直下の余白部分に白フェードを表示する
  // (PropertyRegistrationTabs の scrollableContent と同じパターン)。
  // modalType に依存せず、wrapper 内で実際にスクロールしうる要素を自動検出する。
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [isBodyScrolled, setIsBodyScrolled] = useState(false);
  // フェードオーバーレイの top / height。
  // top は「タイトル直下」(= スクロール要素の offsetTop - marginTop) を指し、
  // height は marginTop ぶんのギャップ + スクロール先頭のフェード幅をまとめて覆う。
  const [overlayTop, setOverlayTop] = useState<number | null>(null);
  const [overlayHeight, setOverlayHeight] = useState<number>(14);

  useEffect(() => {
    // split レイアウト時は右ペインが独立スクロールするため、
    // ヘッダー直下の白フェード処理 (registerModalBody 前提) を無効化する。
    if (!openModal || isSplit) {
      setIsBodyScrolled(false);
      setOverlayTop(null);
      return;
    }
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    // 1) `.registerModalBody` を優先 (明示的に scroll 領域として用意されている)
    // 2) 無ければ wrapper 内の最初の overflow-y: auto/scroll な要素を探す
    const findScrollEl = (): HTMLElement | null => {
      const explicit = wrapper.querySelector<HTMLElement>(".registerModalBody");
      if (explicit) return explicit;
      const candidates = wrapper.querySelectorAll<HTMLElement>("*");
      for (const el of candidates) {
        const cs = window.getComputedStyle(el);
        if (cs.overflowY === "auto" || cs.overflowY === "scroll") {
          return el;
        }
      }
      return null;
    };

    // 現在バインド中の scrollEl とそのクリーンアップを保持。
    // ステップ遷移等で scrollEl が後から出現するモーダルにも追従するため、
    // wrapper 配下を MutationObserver で監視して都度再バインドする。
    let boundEl: HTMLElement | null = null;
    let detachScroll: (() => void) | null = null;
    let ro: ResizeObserver | null = null;

    const cleanupBinding = () => {
      detachScroll?.();
      detachScroll = null;
      ro?.disconnect();
      ro = null;
      boundEl = null;
    };

    const bindTo = (scrollEl: HTMLElement) => {
      const updateOffset = () => {
        // スクロール領域上端の padding-top 部分を白フェードで覆う。
        // この padding 領域はスクロール時にコンテンツが下から上へ通過する場所なので、
        // ここにフェードを置くとヘッダー下に "溶けて消えていく" 表現になる。
        const cs = window.getComputedStyle(scrollEl);
        const paddingTop = parseFloat(cs.paddingTop) || 0;
        setOverlayTop(scrollEl.offsetTop);
        // 完全に padding 分だけだとフェード末端で唐突に切れるため、
        // わずかに content 領域へはみ出させて自然に抜けさせる。
        setOverlayHeight(paddingTop > 0 ? paddingTop + 4 : 14);
      };
      updateOffset();

      const handleScroll = () => {
        setIsBodyScrolled(scrollEl.scrollTop > 0);
      };
      scrollEl.addEventListener("scroll", handleScroll);
      detachScroll = () => scrollEl.removeEventListener("scroll", handleScroll);

      ro = new ResizeObserver(updateOffset);
      ro.observe(wrapper);
      ro.observe(scrollEl);

      boundEl = scrollEl;
      // 直近のスクロール位置を反映 (再バインド直後にゼロ化させない)
      setIsBodyScrolled(scrollEl.scrollTop > 0);
    };

    const reevaluate = () => {
      const found = findScrollEl();
      if (found === boundEl) return;
      cleanupBinding();
      if (found) {
        bindTo(found);
      } else {
        setOverlayTop(null);
        setIsBodyScrolled(false);
      }
    };

    reevaluate();

    const mo = new MutationObserver(reevaluate);
    mo.observe(wrapper, { childList: true, subtree: true });

    return () => {
      mo.disconnect();
      cleanupBinding();
    };
  }, [openModal, modalType, isSplit]);

  return (
    <div>
      <MuiModal
        open={openModal}
        onClose={requestClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          className={`modalBox ${isMobile ? "sp" : ""} ${modalTypeClass} ${wide ? "wide" : ""}`}
          sx={
            dynamicHeight
              ? {
                  height: "auto !important",
                  maxHeight: "calc(100vh - 32px)",
                  ...sx,
                }
              : sx
          }
        >
          <Box className="modalBoxWrapper" ref={wrapperRef}>
            <CustomSectionTitle title={title} addBorder={addBorder} />
            {!hideCloseButton && (
              <span className="closeIcon" onClick={requestClose}>
                <CloseIcon />
              </span>
            )}
            {overlayTop !== null && !isSplit && (
              <Box
                aria-hidden
                sx={{
                  position: "absolute",
                  top: `${overlayTop}px`,
                  left: 0,
                  right: 0,
                  height: `${overlayHeight}px`,
                  pointerEvents: "none",
                  zIndex: 2,
                  background:
                    "linear-gradient(to bottom, rgba(255,255,255,1) 0%, rgba(255,255,255,0.8) 50%, rgba(255,255,255,0) 100%)",
                  opacity: isBodyScrolled ? 1 : 0,
                  transition: "opacity 0.15s ease-out",
                }}
              />
            )}
            {(modalTypeClass === "otherModal" ||
              modalTypeClass === "deleteModal") &&
              bodyText && (
                <Box
                  sx={{
                    textAlign: bodyTextAlign,
                    padding: { xs: "16px 0 20px 0", sm: "22px 0 30px 0" },
                    ...sx,
                  }}
                >
                  {bodyText.split("\n").map((line: string, index: number) => (
                    <Typography
                      key={index}
                      align={bodyTextAlign}
                      fontSize={{ xs: 12, sm: 14 }}
                      fontWeight={400}
                      sx={{
                        marginBottom:
                          index < bodyText.split("\n").length - 1 ? 1 : 0,
                      }}
                    >
                      {line}
                    </Typography>
                  ))}
                </Box>
              )}
            {isSplit ? (
              <Box className="modalBoxSplit">
                <Box className="modalBoxSplit__left">{leftPane}</Box>
                <Box className="modalBoxSplit__right">{children}</Box>
              </Box>
            ) : (
              children
            )}
          </Box>
        </Box>
      </MuiModal>

      {/* 未保存ガード: dirty 状態で閉じようとした時の破棄確認 (× / 背景 / Esc 共通)。
          既存の離脱確認 (CustomFormNavigationGuard) と同じデザインを踏襲する。
          ※ confirmOnDirtyClose の時だけ描画する。常時描画すると確認用 CustomModal が
            さらに確認用 CustomModal を描画して無限再帰になるため。 */}
      {confirmOnDirtyClose && (
        <CustomModal
          openModal={showDiscardConfirm}
          handleCloseModal={cancelDiscard}
          title="ページ移動の確認"
          bodyText={dirtyCloseMessage}
          modalType="deleteModal"
          bodyTextAlign="left"
          hideCloseButton
          sx={{ padding: "0px !important" }}
        >
          <Box sx={{ display: "flex", justifyContent: "center", gap: "10px" }}>
            <CustomButton
              label="戻る"
              buttonCategory="cancelModal"
              onClick={cancelDiscard}
              sx={{ width: "50% !important" }}
            />
            <CustomButton
              label="移動"
              buttonCategory="confirm"
              onClick={confirmDiscard}
              sx={{ width: "50% !important" }}
            />
          </Box>
        </CustomModal>
      )}
    </div>
  );
};

export default CustomModal;
